import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, ArrowRight, CheckCircle, WarningCircle, CircleNotch } from "@phosphor-icons/react";
import { GhostButton } from "@/components/admin/parts";
import { adminApi } from "@/lib/api";
import { cardCls, fmtDate } from "./constants";
import { StatCard } from "./bits";
import { VanPreview } from "./VanPreview";

function LastRun({ run, running }) {
  if (running)
    return <div className="flex items-center gap-2 text-sm text-[#73bf44]" data-testid="sync-running"><CircleNotch size={16} className="animate-spin" /> Syncing with Monday.com…</div>;
  if (!run) return <div className="text-sm text-neutral-500" data-testid="sync-never">No sync has run yet.</div>;
  const ok = run.status === "success";
  const Icon = ok ? CheckCircle : WarningCircle;
  return (
    <div data-testid="sync-last-run">
      <div className={`flex items-center gap-2 text-sm ${ok ? "text-[#73bf44]" : "text-red-400"}`}><Icon size={17} weight="fill" /> {run.message || run.status}</div>
      <div className="text-xs text-neutral-500 mt-1">{fmtDate(run.finished_at || run.started_at)} · {run.trigger === "cron" ? "automatic daily sync" : "started manually"}</div>
    </div>
  );
}

function RecentRuns({ refreshKey }) {
  const [runs, setRuns] = useState([]);
  useEffect(() => { adminApi.get("/admin/production/runs").then(({ data }) => setRuns(data.filter((r) => r.kind !== "cron-ack"))).catch(() => {}); }, [refreshKey]);
  if (!runs.length) return null;
  return (
    <div className={`${cardCls} p-6`} data-testid="sync-history">
      <div className="font-heading font-semibold text-white mb-3">Recent runs</div>
      <div className="space-y-2">
        {runs.slice(0, 8).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-neutral-800/60 last:border-0 pb-2">
            <span className={r.status === "error" ? "text-red-400" : r.status === "running" ? "text-[#73bf44]" : "text-neutral-300"}>{r.message || r.status}</span>
            <span className="text-xs text-neutral-500">Sync · {r.trigger} · {fmtDate(r.started_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SyncOverview({ status, onAskAi, onGoTo }) {
  const navigate = useNavigate();
  if (!status) return <div className="text-neutral-600">Loading…</div>;
  const c = status.counts;
  const drafts = c.draft_rules;
  return (
    <div className="space-y-6" data-testid="sync-overview">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Vans on the schedule" value={c.board_items} sub="Last pulled from Monday.com" testId="stat-board-items" />
        <StatCard label="Added by sync" value={c.from_production} sub={`${c.caravans} vans in the Caravan Database`} accent="text-[#73bf44]" testId="stat-added" />
        <StatCard label="Open flags" value={c.open_flags} sub="Waiting in Config Discrepancies" accent={c.open_flags ? "text-amber-400" : "text-white"} testId="stat-open-flags" />
        <StatCard label="Rules" value={c.rules} sub={`${drafts} draft${drafts === 1 ? "" : "s"} waiting for approval`} testId="stat-rules" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className={`${cardCls} p-6`}>
          <div className="font-heading font-semibold text-white mb-3">Last sync</div>
          <LastRun run={status.last_sync} running={status.running} />
          <div className="text-xs text-neutral-500 mt-4">{status.config.auto_sync ? "Runs automatically every day at 3:00am (Brisbane)." : "Automatic daily sync is off."} New vans are added; existing vans are never changed — differences are flagged instead.</div>
          {c.open_flags > 0 && <GhostButton className="mt-4" onClick={() => navigate("/admin/discrepancies")} data-testid="go-to-flags">Review {c.open_flags} flag(s) <ArrowRight size={14} /></GhostButton>}
        </div>
        <div className={`${cardCls} p-6`}>
          <div className="font-heading font-semibold text-white mb-1">Model codes without base specs</div>
          <p className="text-xs text-neutral-500 mb-3">No base-specs rule matches vans with these codes yet. Click one to ask the Rules Assistant.</p>
          {status.unmapped_codes.length === 0 ? <div className="text-sm text-[#73bf44]" data-testid="unmapped-none">Every model code gets base specs from a rule.</div> : (
            <div className="flex flex-wrap gap-2" data-testid="unmapped-codes">
              {status.unmapped_codes.map((u) => (u.code === "(blank)" ? (
                <span key={u.code} data-testid="unmapped-blank" title="Board items with no model code" className="inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-full border border-neutral-700 text-neutral-500">
                  no code <span className="text-neutral-600">({u.count})</span>
                </span>
              ) : (
                <button key={u.code} onClick={() => onAskAi(`Model code ${u.code} (${u.count} vans) has no base specs yet. What do you need from me to set it up?`)} data-testid={`unmapped-${u.code}`} className="inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors">
                  <Sparkle size={12} weight="fill" /> {u.code} <span className="text-neutral-500">({u.count})</span>
                </button>
              )))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-neutral-800">
            {drafts > 0 && <GhostButton onClick={() => onGoTo("rules")} data-testid="review-drafts">Review {drafts} draft rule(s) <ArrowRight size={14} /></GhostButton>}
            <GhostButton onClick={() => onGoTo("rules")} data-testid="open-rules"><Sparkle size={15} /> Open the Rules Assistant</GhostButton>
          </div>
        </div>
      </div>
      <VanPreview />
      <RecentRuns refreshKey={status.latest_run?.id + String(status.running)} />
    </div>
  );
}
